import { useCallback } from "react";
import { useTrackAnalyticsEventMutation } from "../../redux/actions/Customer/analyticsCustomer";
import {
  getAnalyticsSessionId,
  hasTrackedBannerView,
  markBannerViewTracked,
} from "../../utils/analytics";

export const useAnalytics = () => {
  const [trackAnalyticsEvent] = useTrackAnalyticsEventMutation();

  // ------------------------------------------------------------
  // Banner View
  // ------------------------------------------------------------

  const trackBannerView = useCallback(
    async (bannerId: number) => {
      if (!bannerId) return;

      // Prevent duplicate impression for this banner
      if (hasTrackedBannerView(bannerId)) {
        return;
      }

      // Mark before API call to prevent duplicate requests
      markBannerViewTracked(bannerId);

      try {
        await trackAnalyticsEvent({
          eventType: 1,
          entityType: "Banner",
          entityId: bannerId,
          sessionId: getAnalyticsSessionId(),
        }).unwrap();
      } catch (error) {
        console.error("Failed to track banner view:", error);

        // Allow retry if API failed
        sessionStorage.removeItem(`analytics_banner_view_${bannerId}`);
      }
    },
    [trackAnalyticsEvent],
  );

  // ------------------------------------------------------------
  // Banner Click
  // ------------------------------------------------------------

  const trackBannerClick = useCallback(
    async (bannerId: number) => {
      if (!bannerId) return;

      try {
        await trackAnalyticsEvent({
          eventType: 2,
          entityType: "Banner",
          entityId: bannerId,
          sessionId: getAnalyticsSessionId(),
        }).unwrap();
      } catch (error) {
        console.error("Failed to track banner click:", error);
      }
    },
    [trackAnalyticsEvent],
  );

  // ------------------------------------------------------------
  // Product View
  // ------------------------------------------------------------

  const trackProductView = useCallback(
    async (productId: number) => {
      if (!productId) return;

      try {
        await trackAnalyticsEvent({
          eventType: 3,
          entityType: "Product",
          entityId: productId,
          sessionId: getAnalyticsSessionId(),
        }).unwrap();
      } catch (error) {
        console.error("Failed to track product view:", error);
      }
    },
    [trackAnalyticsEvent],
  );

  // ------------------------------------------------------------
  // Search
  // ------------------------------------------------------------

  const trackSearch = useCallback(
    async (searchTerm: string) => {
      const text = searchTerm.trim();

      if (!text) return;

      try {
        await trackAnalyticsEvent({
          eventType: 4,
          searchTerm: text,
          sessionId: getAnalyticsSessionId(),
        }).unwrap();
      } catch (error) {
        console.error("Failed to track search:", error);
      }
    },
    [trackAnalyticsEvent],
  );

  return {
    trackBannerView,
    trackBannerClick,
    trackProductView,
    trackSearch,
  };
};
